import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChordCard, ChordChip } from '@/components/ui/cifras/chord-card';
import { sectionColor } from '@/components/ui/cifras/section-label';
import { SongSheet } from '@/components/ui/cifras/song-sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Segmented } from '@/components/ui/segmented';
import { Textarea } from '@/components/ui/textarea';
import {
  chordShapes,
  chordsOf,
  findContentIssues,
  importChordTxt,
  parseSong,
  type SectionKind,
} from '@/lib/song';

type Tab = 'edit' | 'preview';

const SECTION_BUTTONS: { name: string; kind: SectionKind }[] = [
  { name: 'Verso', kind: 'verso' },
  { name: 'Pré-refrão', kind: 'pre-refrao' },
  { name: 'Refrão', kind: 'refrao' },
  { name: 'Ponte', kind: 'outra' },
  { name: 'Final', kind: 'outra' },
];

type Edit = { text: string; cursor: number };
type EditFn = (value: string, start: number, end: number) => Edit;

const lineStart = (value: string, position: number) =>
  value.lastIndexOf('\n', position - 1) + 1;
const lineEnd = (value: string, position: number) => {
  const index = value.indexOf('\n', position);
  return index === -1 ? value.length : index;
};

/** Envolve a seleção com `open`/`close`; sem seleção, deixa o cursor entre os dois. */
const wrap =
  (open: string, close: string): EditFn =>
  (value, start, end) => {
    const selected = value.slice(start, end);
    return {
      text: value.slice(0, start) + open + selected + close + value.slice(end),
      cursor: start + open.length + (selected ? selected.length + close.length : 0),
    };
  };

const insert =
  (snippet: string): EditFn =>
  (value, start, end) => ({
    text: value.slice(0, start) + snippet + value.slice(end),
    cursor: start + snippet.length,
  });

/** `*2x*` no fim da linha do cursor; se a linha já tem, soma uma vez (`*3x*`, `*4x*`…). */
const repeatLine: EditFn = (value, start) => {
  const from = lineStart(value, start);
  const to = lineEnd(value, start);
  const line = value.slice(from, to);
  const current = Number(line.match(/\*(\d+)x\*\s*$/i)?.[1] ?? 1);
  const next =
    `${line.replace(/\s*\*\d+x\*\s*$/i, '').trimEnd()} *${current + 1}x*`.trimStart();
  return {
    text: value.slice(0, from) + next + value.slice(to),
    cursor: from + next.length,
  };
};

/** Linha em branco depois da linha do cursor. */
const stanzaBreak: EditFn = (value, start) => {
  const to = lineEnd(value, start);
  return { text: `${value.slice(0, to)}\n\n${value.slice(to)}`, cursor: to + 2 };
};

/** Cabeçalho `!!*Nome*` em linha própria, logo depois da linha do cursor. */
const sectionHeader =
  (name: string): EditFn =>
  (value, start) => {
    const before = value.slice(0, lineEnd(value, start)).trimEnd();
    const after = value.slice(lineEnd(value, start)).replace(/^\s*\n/, '');
    const header = `${before ? '\n\n' : ''}!!*${name}*\n`;
    return { text: before + header + after, cursor: before.length + header.length };
  };

const SHORTCUTS: {
  label: string;
  token: string;
  description: string;
  example: string;
  edit: EditFn;
}[] = [
  {
    label: 'Acorde',
    token: '[ ]',
    description:
      'Acorde que entra na sílaba logo depois dele. Coloque o cursor antes da sílaba e digite o acorde.',
    example: 'Não [G]valeria a pena se arris[F]car',
    edit: wrap('[', ']'),
  },
  {
    label: 'Acorde solto',
    token: '| |',
    description:
      'Acorde no meio da linha, sem letra embaixo. Bom para intro, solo e passagem instrumental.',
    example: '|Am| |F| |C| |G|',
    edit: wrap('|', '|'),
  },
  {
    label: 'Sem acorde',
    token: '[N.C.]',
    description: 'Pausa na harmonia (no chord): a banda para e fica só a voz.',
    example: '[N.C.]Só a voz nesse trecho',
    edit: insert('[N.C.]'),
  },
  {
    label: 'Repetição',
    token: '*2x*',
    description:
      'Fica no fim da linha e mostra quantas vezes ela repete. Clique de novo para 3x, 4x…',
    example: 'Santo, santo é o Senhor *2x*',
    edit: repeatLine,
  },
  {
    label: 'Destaque',
    token: '* *',
    description: 'Deixa um trecho da letra em negrito, como uma indicação de quem canta.',
    example: '*Todos:* Aleluia',
    edit: wrap('*', '*'),
  },
  {
    label: 'Nova estrofe',
    token: '↵',
    description: 'Linha em branco: separa estrofes dentro da mesma seção.',
    example: '(linha vazia entre as estrofes)',
    edit: stanzaBreak,
  },
];

type Props = {
  value: string;
  onChange: (value: string) => void;
  error?: string;
};

/**
 * Card "Cifra" do editor: aba Editar (inserir seção, textarea, acordes detectados, avisos)
 * e aba Visualizar (`SongSheet` + diagramas). O conteúdo é o texto cru; a forma canônica
 * (`cleanSongContent`) é aplicada pela API ao salvar.
 */
export const SongContentEditor: React.FC<Props> = ({ value, onChange, error }) => {
  const [tab, setTab] = useState<Tab>('edit');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');

  const sections = useMemo(() => parseSong(value), [value]);
  const chords = useMemo(() => chordsOf(value), [value]);
  const issues = useMemo(() => findContentIssues(value), [value]);
  const withoutShape = chords.filter((chord) => !chordShapes[chord]);
  const lineCount = sections.reduce((total, section) => total + section.lines.length, 0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Última seleção no textarea; antes do primeiro clique nele, os atalhos vão para o fim. */
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  const apply = (edit: EditFn) => {
    const { start, end } = selectionRef.current ?? {
      start: value.length,
      end: value.length,
    };
    const result = edit(value, start, end);
    onChange(result.text);
    selectionRef.current = { start: result.cursor, end: result.cursor };

    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(result.cursor, result.cursor);
    });
  };

  const handleImport = () => {
    if (!importText.trim()) return;

    if (value.trim() && !window.confirm('Substituir a cifra atual pelo texto colado?')) {
      return;
    }

    onChange(importChordTxt(importText));
    setTab('preview');
    setImportOpen(false);
    setImportText('');
  };

  return (
    <section className="overflow-hidden rounded-[16px] border border-border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-border border-b px-[26px] py-5">
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="font-display font-extrabold text-[18px] text-white uppercase tracking-tight">
            Cifra
          </span>
          <span className="text-[13px] text-muted-foreground">
            {sections.length} seções · {lineCount} linhas · {chords.length} acordes
            {withoutShape.length > 0 && ` · ${withoutShape.length} sem diagrama`}
          </span>
        </span>

        <Button type="button" variant="outline" onClick={() => setImportOpen(true)}>
          <Icon name="clipboard-paste" />
          Importar TXT
        </Button>

        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: 'edit', label: 'Editar' },
            { value: 'preview', label: 'Visualizar' },
          ]}
        />
      </div>

      {tab === 'edit' ? (
        <div className="flex flex-col gap-4 p-[26px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="col-label mr-1 text-faint">Inserir seção</span>
            {SECTION_BUTTONS.map(({ name, kind }) => (
              <button
                key={name}
                type="button"
                onClick={() => apply(sectionHeader(name))}
                className="h-7 rounded-full border px-3 font-bold text-[11px] uppercase tracking-[0.1em] transition-colors hover:bg-raised"
                style={{ color: sectionColor(kind), borderColor: sectionColor(kind) }}
              >
                {name}
              </button>
            ))}
          </div>

          <p className="-mt-2 text-[12.5px] text-muted-foreground">
            A seção entra abaixo da linha do cursor, como{' '}
            <code className="text-accent-soft">!!*Refrão*</code> numa linha própria.
          </p>

          <div className="flex flex-col gap-2">
            <span className="col-label text-faint">
              Atalhos — clique para aplicar no cursor
            </span>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {SHORTCUTS.map(({ label, token, description, example, edit }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => apply(edit)}
                  className="flex flex-col gap-1 rounded-[12px] border border-border bg-raised px-3.5 py-3 text-left transition-colors hover:border-primary"
                >
                  <span className="flex items-center gap-2">
                    <code className="rounded-[6px] bg-sunken px-1.5 py-0.5 font-bold font-mono text-[12.5px] text-accent-soft">
                      {token}
                    </code>
                    <span className="font-bold text-[13px] text-foreground">{label}</span>
                  </span>
                  <span className="text-[12px] text-muted-foreground leading-snug">
                    {description}
                  </span>
                  <code className="truncate font-mono text-[11.5px] text-faint">
                    {example}
                  </code>
                </button>
              ))}
            </div>
          </div>

          <Textarea
            ref={textareaRef}
            onSelect={(event) =>
              (selectionRef.current = {
                start: event.currentTarget.selectionStart,
                end: event.currentTarget.selectionEnd,
              })
            }
            value={value}
            onChange={(event) => onChange(event.target.value)}
            spellCheck={false}
            aria-invalid={Boolean(error)}
            placeholder="!!*Verso*&#10;Não [G]valeria a pena se arris[F]car"
            className="min-h-[560px] rounded-[12px] bg-sunken font-mono text-[13.5px] leading-relaxed"
          />
          {error && <p className="font-medium text-destructive text-xs">{error}</p>}

          {issues.length > 0 && (
            <ul
              className="flex flex-col gap-1 rounded-[10px] border border-dashed px-3.5 py-2.5 text-[12.5px]"
              style={{ borderColor: 'var(--section-pre-refrao)' }}
            >
              {issues.slice(0, 6).map((issue, index) => (
                <li key={`${issue.line}-${index}`} className="text-muted-foreground">
                  <span
                    className="font-bold"
                    style={{ color: 'var(--section-pre-refrao)' }}
                  >
                    Linha {issue.line}:
                  </span>{' '}
                  {issue.message}
                </li>
              ))}
              {issues.length > 6 && (
                <li className="text-faint">… e mais {issues.length - 6} aviso(s)</li>
              )}
            </ul>
          )}

          {chords.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="col-label text-faint">Acordes detectados</span>
              <div className="flex flex-wrap gap-1.5">
                {chords.map((chord) => (
                  <ChordChip
                    key={chord}
                    name={chord}
                    hasShape={Boolean(chordShapes[chord])}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-8 p-[26px] lg:grid-cols-[1fr_220px]">
          <div className="min-w-0">
            {value.trim() ? (
              <SongSheet content={value} />
            ) : (
              <p className="text-muted-foreground text-sm">Nada para visualizar ainda.</p>
            )}
          </div>
          <div className="grid h-fit grid-cols-2 gap-2 lg:sticky lg:top-[88px]">
            {chords.map((chord) => (
              <ChordCard key={chord} name={chord} shape={chordShapes[chord]} />
            ))}
          </div>
        </div>
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Importar TXT</DialogTitle>
            <DialogDescription>
              Cole a cifra em texto (acordes na linha de cima da letra). Ela é convertida
              para o formato do editor.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            autoFocus
            value={importText}
            onChange={(event) => setImportText(event.target.value)}
            spellCheck={false}
            placeholder={'[Verso]\nG              D\nNão valeria a pena se arriscar'}
            className="min-h-[420px] rounded-[12px] bg-sunken font-mono text-[13.5px] leading-relaxed"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImportOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" disabled={!importText.trim()} onClick={handleImport}>
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
